import { useState } from 'react'
import { MessageCircleQuestion, ListChecks, Bot, Check, Play } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import {
  QUIET_OVERFLOW_MENU_COLLISION_PADDING,
  QUIET_OVERFLOW_MENU_CONTENT_CLASS,
} from '@/lib/menu-chrome'
import { testIds } from '@/lib/test-ids'

export type ChatMode = 'ask' | 'plan' | 'agent'

const CHAT_MODE_OPTIONS: {
  value: ChatMode
  label: string
  Icon: typeof Bot
  description: string
}[] = [
  {
    value: 'ask',
    label: 'Ask',
    Icon: MessageCircleQuestion,
    description: 'Read-only. Answers questions, no changes.',
  },
  {
    value: 'plan',
    label: 'Plan',
    Icon: ListChecks,
    description: 'Researches and proposes a plan, no changes. Use Build to create files.',
  },
  {
    value: 'agent',
    label: 'Agent',
    Icon: Bot,
    description: 'Full access. Reads and makes changes.',
  },
]

function isChatMode(value: string): value is ChatMode {
  return value === 'ask' || value === 'plan' || value === 'agent'
}

/**
 * Ask / Plan / Agent picker. Visual language matches WorkspaceScopeSelector
 * (header + bordered rows); selection is exclusive (one mode at a time).
 * When in Plan, optional Build CTA switches to Agent and runs the plan.
 */
export function ChatModeSelector({
  value,
  onChange,
  onBuild,
  buildDisabled,
}: {
  value: ChatMode
  onChange: (mode: ChatMode) => void
  /** Plan mode: leave plan and implement (create/edit docs). */
  onBuild?: () => void
  buildDisabled?: boolean
}) {
  const [open, setOpen] = useState(false)
  const current = CHAT_MODE_OPTIONS.find((opt) => opt.value === value) ?? CHAT_MODE_OPTIONS[2]
  const { Icon, label, description } = current
  const showBuild = value === 'plan' && typeof onBuild === 'function'

  return (
    <div className="flex min-w-0 items-center gap-1">
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            data-testid={testIds.chatPane.modeSelector}
            className={cn(
              'inline-flex h-8 w-auto min-w-0 shrink items-center gap-1 rounded-md border border-border bg-background px-1.5 text-xs shadow-xs',
              '@[300px]/compose:max-w-[min(100%,7rem)] @[300px]/compose:gap-1.5 @[300px]/compose:px-2',
              'justify-start text-muted-foreground hover:bg-muted hover:text-foreground transition-colors',
            )}
            title={description}
            aria-label={`Mode: ${label}`}
          >
            <Icon className="h-3 w-3 shrink-0 opacity-70" aria-hidden />
            <span className="hidden min-w-0 truncate text-left text-xs @[300px]/compose:inline">{label}</span>
            <svg
              className="hidden h-3 w-3 shrink-0 opacity-50 @[300px]/compose:block"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className={QUIET_OVERFLOW_MENU_CONTENT_CLASS}
          collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <div className="mb-1.5 px-1.5">
            <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              Chat mode
            </p>
            <p className="text-[10px] text-muted-foreground/60">
              Choose how the assistant can act
            </p>
          </div>
          <div className="flex flex-col gap-1">
            {CHAT_MODE_OPTIONS.map((opt) => {
              const active = opt.value === value
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    if (isChatMode(opt.value)) onChange(opt.value)
                    setOpen(false)
                  }}
                  aria-pressed={active}
                  className={cn(
                    'flex w-full items-start gap-2 rounded-md border px-2 py-1.5 text-left text-xs transition-all duration-100',
                    active
                      ? 'border-border bg-accent/40 text-foreground opacity-100'
                      : 'border-border/60 text-muted-foreground opacity-70 hover:opacity-100 hover:bg-accent/30',
                  )}
                >
                  <opt.Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-1.5 font-medium text-foreground">
                      {opt.label}
                      {active ? <Check className="h-3 w-3 shrink-0 opacity-70" aria-hidden /> : null}
                    </span>
                    <span className="mt-0.5 block text-[10px] leading-snug text-muted-foreground">
                      {opt.description}
                    </span>
                  </span>
                </button>
              )
            })}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
      {showBuild ? (
        <button
          type="button"
          data-testid={testIds.chatPane.planBuildButton}
          disabled={buildDisabled}
          onClick={onBuild}
          title="Switch to Agent and create the files from this plan"
          aria-label="Build: switch to Agent and implement the plan"
          className={cn(
            'inline-flex h-8 shrink-0 items-center gap-1 rounded-md border border-primary/40 bg-primary/10 px-2 text-xs font-medium text-primary',
            'hover:bg-primary/15 transition-colors',
            'disabled:pointer-events-none disabled:opacity-50',
          )}
        >
          <Play className="h-3 w-3 shrink-0" aria-hidden />
          Build
        </button>
      ) : null}
    </div>
  )
}
